import { X, Trash2 } from 'lucide-react';
import { DojosRow, SeasonsRow } from '../types/database';
import { ResetState } from '../modules/stats/resetService';

interface ResetDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  resetState: ResetState;
  setResetState: (state: ResetState) => void;
  dojos: DojosRow[];
  seasons: SeasonsRow[];
  previewCount: number;
  onDojoSelect: (dojoId: string) => void;
  onSeasonSelect: (seasonId: string) => void;
  onSubmit: () => void;
}

export default function ResetDataModal({
  isOpen,
  onClose,
  resetState,
  setResetState,
  dojos,
  seasons,
  previewCount,
  onDojoSelect,
  onSeasonSelect,
  onSubmit,
}: ResetDataModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
      <div className="bg-gradient-to-br from-[#2A1F3D] to-[#1E1A30] rounded-2xl max-w-md w-full border border-red-500/30 shadow-2xl shadow-red-500/20 max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-red-500/20 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-400" />
            重置战绩数据
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-avalon-text transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-avalon-text text-sm mb-2">选择道场</label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              <button
                onClick={() => onDojoSelect('all')}
                className={`w-full p-2 rounded-lg text-left text-sm transition-colors ${
                  resetState.selectedDojos.includes('all')
                    ? 'bg-red-500/30 border border-red-500/50 text-white'
                    : 'bg-avalon-card text-avalon-text hover:bg-avalon-purple/30'
                }`}
              >
                全部道场
              </button>
              {dojos.map(dojo => (
                <button
                  key={dojo.id}
                  onClick={() => onDojoSelect(String(dojo.id))}
                  className={`w-full p-2 rounded-lg text-left text-sm transition-colors ${
                    resetState.selectedDojos.includes(String(dojo.id))
                      ? 'bg-red-500/30 border border-red-500/50 text-white'
                      : 'bg-avalon-card text-avalon-text hover:bg-avalon-purple/30'
                  }`}
                >
                  {dojo.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-avalon-text text-sm mb-2">选择赛季</label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              <button
                onClick={() => onSeasonSelect('all')}
                className={`w-full p-2 rounded-lg text-left text-sm transition-colors ${
                  resetState.selectedSeasons.includes('all')
                    ? 'bg-red-500/30 border border-red-500/50 text-white'
                    : 'bg-avalon-card text-avalon-text hover:bg-avalon-purple/30'
                }`}
              >
                全部赛季
              </button>
              {seasons.map(season => (
                <button
                  key={season.id}
                  onClick={() => onSeasonSelect(String(season.id))}
                  className={`w-full p-2 rounded-lg text-left text-sm transition-colors ${
                    resetState.selectedSeasons.includes(String(season.id))
                      ? 'bg-red-500/30 border border-red-500/50 text-white'
                      : 'bg-avalon-card text-avalon-text hover:bg-avalon-purple/30'
                  }`}
                >
                  {season.name}
                </button>
              ))}
            </div>
          </div>

          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <p className="text-red-400 text-sm">
              将要删除 <span className="text-white font-bold">{previewCount}</span> 条战绩记录
            </p>
            <p className="text-red-400/70 text-xs mt-1">⚠️ 此操作不可恢复</p>
          </div>

          <div>
            <label className="block text-avalon-text text-sm mb-2">输入密码</label>
            <input
              type="password"
              value={resetState.passwordInput}
              onChange={(e) => setResetState({ ...resetState, passwordInput: e.target.value })}
              placeholder="请输入密码"
              className="w-full p-3 rounded-lg bg-white/5 text-white border border-white/20 focus:border-avalon-purple outline-none"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-lg bg-avalon-card text-white hover:bg-avalon-purple/50 transition-colors"
            >
              取消
            </button>
            <button
              onClick={onSubmit}
              disabled={!resetState.passwordInput.trim()}
              className="flex-1 py-3 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              删除
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
